import type { Coordinate } from '@/types/properties';
import type { RenderNodesMap } from '@/types/commontypes';
import { ShapeType, type RFCInteractiveItem } from '@/types/commontypes';
import { isDefined } from 'react-financial-charts';
import { entries, keys } from './utils';

type MatchResult = {
  type: string;
  shapeUuid: string;
  index: number;
  matchedPoint: Coordinate;
};

export function findMatchingShape(
  clickedPosition: Coordinate,
  renderNodes: RenderNodesMap,
  tolerance = 2,
): MatchResult[] {
  const [clickX, clickY] = clickedPosition;

  function isClose(x: number, y: number): boolean {
    return Math.abs(x - clickX) <= tolerance && Math.abs(y - clickY) <= tolerance;
  }

  const results: MatchResult[] = [];

  for (const [_eachChartId, chartShapes] of entries(renderNodes)) {
    if (!chartShapes || keys(chartShapes).length === 0) continue;
    for (const [type, shapes] of entries(chartShapes)) {
      if (!Array.isArray(shapes) || shapes.length === 0) continue;
      shapes?.forEach((shape, index) => {
        // RFC 경계: 다양한 variant의 좌표 필드에 제네릭하게 접근하기 위해 loose 타입으로 캐스트
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        const s = shape as unknown as RFCInteractiveItem & {
          position?: [number, number];
          yValue?: number;
          xValue?: number;
          startXY?: [number, number];
          endXY?: [number, number];
          endXY2?: [number, number];
          x1?: number;
          y1?: number;
          x2?: number;
          y2?: number;
          points?: [number, number][];
        };
        // Special case: Text shapes
        if (type === ShapeType.TEXT && Array.isArray(s.position)) {
          const [x, y] = s.position;
          if (isClose(x, y)) {
            results.push({
              type,
              shapeUuid: shape.uuid,
              index,
              matchedPoint: [x, y],
            });
          }
          return;
        }

        // Handle HorzLine shapes by comparing yValue
        if (type === ShapeType.HORZ_LINE && typeof s.yValue === 'number') {
          if (Math.abs(s.yValue - clickY) <= tolerance) {
            results.push({
              type,
              shapeUuid: shape.uuid,
              index,
              matchedPoint: [clickX, s.yValue],
            });
          }
          return;
        }

        // Handle VertLine shapes by comparing xValue
        if (type === ShapeType.VERT_LINE && typeof s.xValue === 'number') {
          if (Math.abs(s.xValue - clickX) <= tolerance) {
            results.push({
              type,
              shapeUuid: shape.uuid,
              index,
              matchedPoint: [s.xValue, clickY],
            });
          }
          return;
        }

        // Default point proximity check for other shape types
        const pointsToCheck: Coordinate[] = [];

        [s.start, s.end, s.startXY, s.endXY, s.endXY2].forEach(p => {
          const coord = toCoordinate(p);
          if (coord) pointsToCheck.push(coord);
        });
        if (typeof s.x1 === 'number' && typeof s.y1 === 'number') {
          pointsToCheck.push([s.x1, s.y1]);
        }

        if (typeof s.x2 === 'number' && typeof s.y2 === 'number') {
          pointsToCheck.push([s.x2, s.y2]);
        }

        if (Array.isArray(s.points)) {
          s.points.forEach((p: unknown) => {
            const coord = toCoordinate(p);
            if (coord) pointsToCheck.push(coord);
          });
        }

        for (const [x, y] of pointsToCheck) {
          if (isClose(x, y)) {
            results.push({
              type,
              shapeUuid: shape.uuid,
              index,
              matchedPoint: [x, y],
            });
            break;
          }
        }
      });
    }
  }
  return results;
}

export const MeasureBgColor = (
  completedRect: { end?: { yValue?: number }; start?: { yValue?: number } } | null | undefined,
) => {
  if (isDefined(completedRect)) {
    const { end, start } = completedRect;
    return (end?.yValue ?? 0) < (start?.yValue ?? 0) ? 'rgba(239, 83, 80, 0.3)' : 'rgba(38, 166, 154, 0.3)';
  }
  return undefined;
};

export const toCoordinate = (value: unknown): Coordinate | null => {
  if (Array.isArray(value) && value.length === 2) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    return value as Coordinate;
  }

  if (
    value &&
    typeof value === 'object' &&
    typeof (value as { x?: unknown }).x === 'number' &&
    typeof (value as { y?: unknown }).y === 'number'
  ) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const v = value as { x: number; y: number };
    return [v.x, v.y];
  }

  return null;
};
